import { useRef, useState } from 'react'
import AppLoading from '../../../../components/atoms/AppLoading/AppLoading'
import { AppTable } from '../../../../components/molecules/AppTable/AppTable'
import { ContentTableItems } from '../../../../components/molecules/ContentTableItem/ContentTableItems'
import AppAvatar from '../../../../components/atoms/AppAvatar/AppAvatar'
import AppModal from '../../../../components/atoms/AppModal/AppModal'
import TableAction from '../../../../components/molecules/TableAction/TableAction'
import { useNavigate } from 'react-router-dom'
import { User } from '../../../domain/user'
import { Snackbar } from 'components/atoms/Snackbar/Snackbar'

const titleTableBase = [
    {
        id: 'usuario',
        title: 'Usuario',
        sortable: true,
        sortOrder: null,
        className: 'text-left px-4 font-medium text-sm',
    },
    {
        id: 'rol',
        title: 'Rol',
        sortable: false,
        align: 'center',
    },
    {
        id: 'warehouse',
        title: 'Warehouse',
        sortable: false,
        align: 'center',
    },
    {
        id: 'created_at',
        title: 'Fecha de creación',
        sortable: false,
        align: 'center',
    },
    { id: 'acciones', title: '', sortable: false, align: 'right' },
]

type UserListProps = {
    list: User[]
    onSubmit?: () => void
    onActive?: () => void
    onDelete?: (userId: number) => Promise<void>
    onSortColumn?: () => void
}

interface SnackbarHandle {
    showModal: (value: boolean) => void
}

export default function UserList({
    onDelete = async () => {},
    list,
}: UserListProps) {
    const [loadingFilters] = useState(false)
    const navigate = useNavigate()
    const [sortConfig, setSortConfig] = useState<{
        column: string | null
        order: 'asc' | 'desc' | null
    }>({
        column: null,
        order: null,
    })
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
    const snackbarRef = useRef<SnackbarHandle>(null)
    const getSortedUsers = () => {
        if (sortConfig.column === null || sortConfig.order === null) {
            return list
        }

        const sorted = [...list].sort((a, b) => {
            let valueA
            let valueB

            switch (sortConfig.column) {
                case 'usuario':
                    valueA = a.name.toLowerCase()
                    valueB = b.name.toLowerCase()
                    break
                case 'rol':
                    valueA = a.roleId
                    valueB = b.roleId
                    break
                default:
                    return 0
            }

            if (sortConfig.order === 'asc') {
                return valueA > valueB ? 1 : valueA < valueB ? -1 : 0
            } else {
                return valueA < valueB ? 1 : valueA > valueB ? -1 : 0
            }
        })

        return sorted
    }

    const titleTable = titleTableBase.map((item) => ({
        ...item,
        state:
            sortConfig.column === item.id
                ? sortConfig.order === null
                    ? undefined
                    : sortConfig.order
                : undefined,
    }))

    const onSubmitSortOrder = (state: 'asc' | 'desc', title: string) => {
        const columnId = titleTableBase.find((item) => item.title === title)?.id
        setSortConfig({
            column:
                columnId !== undefined && columnId !== null ? columnId : null,
            order: state,
        })
    }

    const handleConfirmDelete = async () => {
        if (
            selectedUserId !== null &&
            selectedUserId !== undefined &&
            selectedUserId !== ''
        ) {
            try {
                await onDelete(Number(selectedUserId))
                if (snackbarRef.current) {
                    snackbarRef.current.showModal(true)
                }
            } catch (error) {
                console.error('Error al eliminar usuario:', error)
            }
        }
        setIsModalOpen(false)
        setSelectedUserId(null)
    }

    return (
        <main className="">
            <Snackbar
                ref={snackbarRef}
                title="¡Éxito!"
                description="Usuario eliminado exitosamente"
                state="success"
                time={4000}
            />
            <div className="">
                <AppTable
                    dataHead={titleTable}
                    onSortColumn={(state, title) =>
                        onSubmitSortOrder(state, title)
                    }
                    children={
                        loadingFilters ? (
                            <tbody>
                                <tr>
                                    <td colSpan={titleTable.length}>
                                        <div className="flex justify-center h-64 items-center w-full">
                                            <AppLoading />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : list.length === 0 ? (
                            <tbody>
                                <tr>
                                    <td colSpan={titleTable.length}>
                                        <div className="flex justify-center h-75 items-center w-full">
                                            No hay usuarios registrados.
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        ) : (
                            <>
                                {getSortedUsers().map((item) => (
                                    <ContentTableItems
                                        key={item.id}
                                        onClick={() => {
                                            item.id
                                        }}
                                        nodes={[
                                            <div
                                                key={item.id}
                                                className="flex flex-col pl-4"
                                            >
                                                <AppAvatar
                                                    title={item.name}
                                                    description={item.email}
                                                    image={
                                                        'https://ui-avatars.com/api/?background=random&name=' +
                                                        item.name.replace(
                                                            ' ',
                                                            '+'
                                                        )
                                                    }
                                                />
                                            </div>,
                                            <h2
                                                key={item.id}
                                                className="text-center font-normal text-sm "
                                            >
                                                {item.roleId == 1
                                                    ? 'Administrador'
                                                    : 'Operario'}
                                            </h2>,
                                            <h2
                                                key={item.id}
                                                className="text-center font-normal text-sm "
                                            >
                                                {item.warehouse_name}
                                            </h2>,
                                            <h2
                                                key={item.id}
                                                className="text-center font-normal text-sm "
                                            >
                                              {item.createdAt}
                                            </h2>,
                                            <TableAction
                                                key="action-buttons"
                                                onEdit={() => {
                                                    navigate(
                                                        `/users/update/${item.id}`
                                                    )
                                                }}
                                            />,
                                        ]}
                                    />
                                ))}
                            </>
                        )
                    }
                />
            </div>

            <AppModal
                title="Eliminar usuario"
                description="¿Estas seguro de que quieres eliminar a este usuario?"
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                handleConfirm={handleConfirmDelete}
            />
        </main>
    )
}
